import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import TabletMacOutlinedIcon from '@mui/icons-material/TabletMacOutlined';
import BoltIcon from '@mui/icons-material/Bolt';
import { type GridColDef, type GridSortModel } from '@mui/x-data-grid';
import CustomStyledDataGrid from 'components/MuiDataGrid/CustomStyledDataGrid';
import { useAppSelector } from 'store/hook';
import { selectAssociatedAssets } from 'store/asset.slice';
import {
  useTabletHeartbeatSSE,
  type TabletHeartbeat,
} from 'hooks/useTabletHeartbeatSSE';

interface TabletRow {
  id: string;
  device: string;
  bumperId: string;
  heartbeat: TabletHeartbeat;
}

export const TabletsTab = () => {
  const { t } = useTranslation();
  const associatedAssets = useAppSelector(selectAssociatedAssets);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { field: 'device', sort: 'asc' },
  ]);
  const [tabletHeartbeats, setTabletHeartbeats] = React.useState<
    Record<string, TabletHeartbeat>
  >({});

  useTabletHeartbeatSSE(
    React.useCallback((heartbeat: TabletHeartbeat) => {
      setTabletHeartbeats((prev) => ({
        ...prev,
        [heartbeat.device]: heartbeat,
      }));
    }, [])
  );

  const rows = React.useMemo<TabletRow[]>(
    () =>
      Object.entries(tabletHeartbeats).map(([device, heartbeat]) => {
        const asset = associatedAssets.find(
          (a) => a.device?.deviceId?.toLowerCase() === device?.toLowerCase()
        );
        return {
          id: device,
          device,
          bumperId: asset?.bumperNumber ?? '—',
          heartbeat,
        };
      }),
    [tabletHeartbeats, associatedAssets]
  );

  const filteredRows = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.device?.toLowerCase().includes(q) ||
        r.bumperId?.toLowerCase().includes(q)
    );
  }, [rows, searchQuery]);

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'device',
        headerName: t('devices_tab_col_device'),
        minWidth: 200,
        flex: 1,
        filterable: true,
        sortable: true,
      },
      {
        field: 'bumperId',
        headerName: t('bumper_id_10'),
        minWidth: 140,
        flex: 0.8,
        filterable: true,
        sortable: true,
      },
      {
        field: 'battery',
        headerName: t('devices_tab_col_battery'),
        minWidth: 130,
        flex: 0.8,
        sortable: true,
        filterable: false,
        valueGetter: ({ row }: { row: TabletRow }) =>
          row.heartbeat.batteryLevel ?? null,
        renderCell: ({ row }: { row: TabletRow }) => {
          const level = row.heartbeat.batteryLevel;
          if (level === undefined || level === null) return '—';
          const isCharging = row.heartbeat.chargingState === 'charging';
          const color =
            level >= 50 ? '#22c55e' : level >= 20 ? '#eab308' : '#ef4444';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" sx={{ color, fontWeight: 700 }}>
                {level}%
              </Typography>
              {isCharging && (
                <Tooltip title={t('devices_tab_charging_tooltip')}>
                  <BoltIcon sx={{ fontSize: 18, color: '#22c55e' }} />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        field: 'appVersions',
        headerName: t('devices_tab_col_app_versions'),
        minWidth: 260,
        flex: 1.4,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: { row: TabletRow }) => {
          const versions = row.heartbeat.appVersions;
          if (!versions) return '—';
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
              <Chip
                label={`Mgr ${versions.manager}`}
                size="small"
                sx={{ borderRadius: '6px', fontSize: '0.72rem', height: 22 }}
              />
              <Chip
                label={`GUI ${versions.gui}`}
                size="small"
                sx={{ borderRadius: '6px', fontSize: '0.72rem', height: 22 }}
              />
              <Chip
                label={`Edge ${versions.edgeservice}`}
                size="small"
                sx={{ borderRadius: '6px', fontSize: '0.72rem', height: 22 }}
              />
            </Box>
          );
        },
      },
      {
        field: 'connectivity',
        headerName: t('devices_tab_col_connectivity'),
        minWidth: 280,
        flex: 1.4,
        sortable: false,
        filterable: false,
        renderCell: ({ row }: { row: TabletRow }) => {
          const { heartbeat } = row;
          if (!heartbeat.networkType) return '—';
          return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
              <Chip
                label={heartbeat.networkType}
                size="small"
                sx={{
                  borderRadius: '6px',
                  fontSize: '0.72rem',
                  height: 22,
                  textTransform: 'uppercase',
                }}
              />
              <Chip
                label={
                  heartbeat.iotConnected
                    ? t('devices_tab_iot_connected')
                    : t('devices_tab_iot_offline')
                }
                size="small"
                sx={{
                  borderRadius: '6px',
                  fontSize: '0.72rem',
                  height: 22,
                  bgcolor: (th) =>
                    alpha(
                      heartbeat.iotConnected
                        ? th.palette.success.main
                        : th.palette.error.main,
                      0.15
                    ),
                  color: heartbeat.iotConnected ? 'success.main' : 'error.main',
                }}
              />
              <Chip
                label={
                  heartbeat.edgeServiceReachable
                    ? t('devices_tab_edge_reachable')
                    : t('devices_tab_edge_down')
                }
                size="small"
                sx={{
                  borderRadius: '6px',
                  fontSize: '0.72rem',
                  height: 22,
                  bgcolor: (th) =>
                    alpha(
                      heartbeat.edgeServiceReachable
                        ? th.palette.success.main
                        : th.palette.error.main,
                      0.15
                    ),
                  color: heartbeat.edgeServiceReachable
                    ? 'success.main'
                    : 'error.main',
                }}
              />
            </Box>
          );
        },
      },
    ],
    [t]
  );

  const hasData = rows.length > 0;
  const hasResults = filteredRows.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        minWidth: 0,
        height: 'calc(100vh - 260px)',
        border: '1px solid',
        borderColor: (th) => alpha(th.palette.divider, 0.5),
        borderRadius: '10px',
        overflow: 'hidden',
      }}
    >
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
          flexShrink: 0,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={700} color="text.primary">
            {t('tablets_tab')}
          </Typography>
          {hasData && (
            <Typography
              variant="caption"
              sx={{
                px: 1.2,
                py: 0.3,
                borderRadius: '20px',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                color: 'primary.main',
                fontWeight: 700,
              }}
            >
              {searchQuery.trim()
                ? `${filteredRows.length} / ${rows.length}`
                : rows.length}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            placeholder={t('devices_tab_search_placeholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery('');
                    }}
                    edge="end"
                    sx={{ p: 0.25 }}
                  >
                    <ClearIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                fontSize: '0.82rem',
              },
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {!hasData && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (th) => alpha(th.palette.primary.main, 0.1),
                border: '1.5px solid',
                borderColor: (th) => alpha(th.palette.primary.main, 0.3),
                mb: 1,
              }}
            >
              <TabletMacOutlinedIcon
                sx={{ fontSize: 32, color: 'primary.main' }}
              />
            </Box>
            <Typography variant="h6" fontWeight={700} color="text.primary">
              {t('tablets_tab_empty_title')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              sx={{ maxWidth: 300, lineHeight: 1.7 }}
            >
              {t('tablets_tab_empty_subtitle')}
            </Typography>
          </Box>
        )}

        {hasData && !hasResults && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <SearchIcon sx={{ fontSize: 40, color: 'text.disabled' }} />
            <Typography variant="body2" color="text.secondary">
              {t('devices_tab_no_results')}
            </Typography>
          </Box>
        )}

        {hasData && hasResults && (
          <CustomStyledDataGrid
            rows={filteredRows}
            columns={columns}
            disableRowSelectionOnClick
            getRowHeight={() => 48}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            sx={{ flex: 1, minWidth: 0, overflow: 'auto', border: 'none' }}
          />
        )}
      </Box>
    </Box>
  );
};
